import { Component, inject, input, computed } from '@angular/core';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap, of, map, combineLatest } from 'rxjs';
import { UserService, TeamService } from '../../services';
import { fullName } from '../../util';

let nextUniqueId = 0;

/**
 * Renders a single referral recipient (internal user, team, or external email).
 *
 * Kept as its own component so {@link ReferredOutComponent} can render a list of
 * referrals, each resolving its user/team independently.
 */
@Component({
    selector: 'sh-referred-out-item',
    templateUrl: './sh-referred-out-item.component.html',
    styleUrl: './sh-referred-out-item.component.scss',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ReferredOutItemComponent {
    public referredOutEmail = input<string>();
    public referredOutTeam = input<string>();
    public isInternalReferral = input<boolean>();

    private userService = inject(UserService);
    private teamService = inject(TeamService);

    /** Unique id so multiple items rendered side by side keep distinct tooltip targets. */
    protected readonly uid = `referred-out-${nextUniqueId++}`;

    private internalUser = toSignal(
        combineLatest([toObservable(this.isInternalReferral), toObservable(this.referredOutEmail)]).pipe(
            switchMap(([isInternal, email]) =>
                isInternal && email ? this.userService.getUserByEmail(email) : of(undefined)
            )
        )
    );

    protected teamName = toSignal(
        toObservable(this.referredOutTeam).pipe(
            switchMap((team) => (team ? this.teamService.getTeamById(team).pipe(map((t) => t.name)) : of(undefined)))
        )
    );

    protected internalUserName = computed(() => fullName(this.internalUser()));

    protected teamInitials = computed(() => {
        const fullTeamName = this.teamName();
        if (!fullTeamName) return '';
        return fullTeamName
            .split(' ')
            .map((word: string) => word.charAt(0))
            .join('')
            .toUpperCase();
    });
}
